/**
 * A criação do personagem: o nome e os 36 pontos de atributo.
 *
 * <p><b>É aqui que o jogador decide, e só aqui.</b> Depois de criada, a ficha é do
 * mestre — foi a regra combinada para a mesa. Por isso a distribuição acontece antes de
 * a ficha existir: o POST leva o nome e os atributos juntos, e não há um instante em que
 * exista uma ficha do jogador que ele ainda possa reescrever.
 *
 * <p>Todos os atributos começam em <b>zero</b>, e subir custa o número aonde se chega —
 * de 0 para 1 custa 1, de 3 para 4 custa 4. É o que faz espalhar ser barato e
 * especializar ser caro, e é a mesma regra do jogo.
 *
 * <p><b>Sobrar ponto é permitido.</b> A tela avisa, e deixa criar: numa mesa
 * experimental, guardar pontos para ver a primeira sessão é uma escolha legítima — e
 * travar o botão obrigaria quem ainda não decidiu a gastar no escuro.
 */
import { useEffect, useState } from 'react';

import { ATRIBUTOS } from '../gamedesign/atributos';
import {
  PONTOS_DE_ATRIBUTO,
  custoDoProximoPonto,
  fichaEmBranco,
  pontosRestantes,
} from './fichaDeMistikDream';
import type { Ficha } from './fichaDeMistikDream';
import './fichas.css';

export function CriarPersonagem({
  criando,
  erro,
  aoFechar,
  aoCriar,
}: {
  criando: boolean;
  erro?: string;
  aoFechar: () => void;
  aoCriar: (nome: string, conteudo: Ficha) => void;
}) {
  const [nome, setNome] = useState('');
  const [f, setF] = useState<Ficha>(fichaEmBranco);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aoFechar]);

  const restantes = pontosRestantes(f.atributos);

  const trocar = (sigla: string, novo: number) =>
    setF((a) => ({ ...a, atributos: { ...a.atributos, [sigla]: novo } }));

  return (
    <div className="fd-visor" onClick={aoFechar}>
      <form
        className="fd-caixa criar"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (nome.trim()) aoCriar(nome.trim(), f);
        }}
      >
        <header className="fd-topo">
          <div className="fd-titulo">
            <input
              className="fd-nome"
              value={nome}
              placeholder="Nome do personagem"
              aria-label="Nome do personagem"
              autoFocus
              onChange={(e) => setNome(e.target.value)}
            />
            <span className="fd-dono">Criando o seu personagem</span>
          </div>
          <button type="button" className="fd-fechar" onClick={aoFechar} title="Fechar (Esc)">
            ✕
          </button>
        </header>

        <div className="fd-corpo">
          <h3 className="fd-divisor">Atributos</h3>
          <p className="fd-pontos">
            <span className="fd-rotulo">Pontos restantes</span>
            <b className="fd-restantes">{restantes}</b>
            <span className="fd-de">de {PONTOS_DE_ATRIBUTO}</span>
            <span className="fd-explica">
              Subir custa o número aonde se chega: de 0 para 1 custa 1, de 3 para 4 custa 4.
            </span>
          </p>

          <div className="fd-atributos">
            {ATRIBUTOS.map((a) => {
              const valor = f.atributos[a.sigla];
              const custo = custoDoProximoPonto(valor);
              return (
                <div className="fd-atributo" key={a.sigla}>
                  <b style={{ color: a.cor }}>{a.sigla}</b>
                  <span className="fd-atributo-nome">{a.nome}</span>
                  <div className="fd-atributo-valores">
                    <button
                      type="button"
                      className="fd-passo"
                      onClick={() => trocar(a.sigla, valor - 1)}
                      disabled={valor <= 0}
                      title={`Devolver ${valor} ponto(s)`}
                      aria-label={`Baixar ${a.nome}`}
                    >
                      −
                    </button>
                    <span className="fd-atributo-base">{valor}</span>
                    <button
                      type="button"
                      className="fd-passo"
                      onClick={() => trocar(a.sigla, valor + 1)}
                      disabled={custo > restantes}
                      title={`Custa ${custo} ponto(s)`}
                      aria-label={`Subir ${a.nome}`}
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="fd-explica solta">
            As perícias começam todas no nível 0 — elas sobem com o uso, e quem dá o XP é o
            mestre. O resto da ficha (HP, combate, itens) ele preenche com você na primeira
            sessão.
          </p>
        </div>

        <footer className="fd-rodape">
          <button type="submit" className="fd-gravar" disabled={!nome.trim() || criando}>
            {criando ? 'Criando…' : 'Criar personagem'}
          </button>
          <span className="fd-pendencia">
            {erro ||
              (restantes > 0
                ? `Você ainda tem ${restantes} ponto(s) para gastar — dá para criar assim mesmo.`
                : 'Pontos todos distribuídos.')}
          </span>
        </footer>
      </form>
    </div>
  );
}
